import { wtUserApi } from 'api/clients';

import type { GenericResponse } from 'commons/modules/generic-response';
import type { PaginationControls } from 'commons/modules/pagination';

import {
  ApiResponse,
  adaptApiToGenericResponse,
  adaptErrorToGenericResponse,
} from '../shared';

import type { InternalUserDto, UserDetailsDto } from './dto';
import {
  mapCreateUserRequestToDto,
  mapDeleteUserParamsToDto,
  mapInternalUserDtoToModel,
  mapInternalUsersListDtoToModel,
  mapUpdateUserRequestToDto,
  mapUserDetailsDtoToModel,
} from './mappers';
import type {
  CreateUserRequest,
  DeleteUserParams,
  InternalUser,
  SearchUsersParams,
  UpdateUserRequest,
  UserDetails,
} from './models';

const USERS_PATH = '/management/users';

export const getUsers = async (
  params: SearchUsersParams,
  pagination: PaginationControls,
): Promise<GenericResponse<ApiResponse<InternalUser[]>>> => {
  const queryParams = {
    email: params.email ?? '',
    role: params.role ?? '',
    order: params.order ?? 'names',
    page: pagination.page,
    pageSize: pagination.pageSize,
  };

  try {
    const response = await wtUserApi.get<ApiResponse<InternalUserDto[]>>(
      USERS_PATH,
      {
        params: queryParams,
      },
    );

    const mapped = mapInternalUsersListDtoToModel(response.data.data || []);

    const mappedResponse: ApiResponse<InternalUser[]> = {
      ...response.data,
      data: mapped,
    };

    return adaptApiToGenericResponse(mappedResponse, response.status);
  } catch (error: any) {
    return adaptErrorToGenericResponse(
      error,
      'No cuenta con apoderados registrados en el sistema.',
    );
  }
};

export const createUser = async (
  body: CreateUserRequest,
): Promise<GenericResponse<ApiResponse<InternalUser>>> => {
  try {
    const response = await wtUserApi.post<ApiResponse<InternalUserDto>>(
      USERS_PATH,
      mapCreateUserRequestToDto(body),
    );

    const mappedResponse: ApiResponse<InternalUser> = {
      ...response.data,
      data: mapInternalUserDtoToModel(response.data.data),
    };

    return adaptApiToGenericResponse(mappedResponse, response.status);
  } catch (error: any) {
    return adaptErrorToGenericResponse(
      error,
      'Ocurrió un error al crear el usuario',
    );
  }
};

export const updateUser = async (
  body: UpdateUserRequest,
): Promise<GenericResponse<ApiResponse<InternalUser>>> => {
  try {
    const response = await wtUserApi.put<ApiResponse<InternalUserDto>>(
      USERS_PATH,
      mapUpdateUserRequestToDto(body),
    );

    const mappedResponse: ApiResponse<InternalUser> = {
      ...response.data,
      data: mapInternalUserDtoToModel(response.data.data),
    };

    return adaptApiToGenericResponse(mappedResponse, response.status);
  } catch (error: any) {
    return adaptErrorToGenericResponse(
      error,
      'Ocurrió un error al actualizar el usuario',
    );
  }
};

export const deleteUser = async (
  params: DeleteUserParams,
): Promise<GenericResponse<ApiResponse<unknown>>> => {
  try {
    const response = await wtUserApi.put<ApiResponse<unknown>>(
      `${USERS_PATH}/`,
      mapDeleteUserParamsToDto(params),
    );

    return adaptApiToGenericResponse(response.data, response.status);
  } catch (error: any) {
    return adaptErrorToGenericResponse(
      error,
      'Ocurrió un error al eliminar el usuario',
    );
  }
};

export const getUserDetails = async (
  amib: string,
): Promise<GenericResponse<ApiResponse<UserDetails>>> => {
  try {
    const response = await wtUserApi.get<UserDetailsDto>(
      `${USERS_PATH}/details`,
      { params: { amib } },
    );

    const mapped = mapUserDetailsDtoToModel(response.data);

    const mappedResponse: ApiResponse<UserDetails> = {
      data: mapped,
      status: 'OK',
      message: 'OK',
      pagination: null,
    };

    return adaptApiToGenericResponse(mappedResponse, response.status);
  } catch (error: any) {
    return adaptErrorToGenericResponse(
      error,
      'Ocurrió un error al obtener los datos del cliente',
    );
  }
};
